import Button from '@rio-cloud/rio-uikit/Button';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import OverlayTrigger from '@rio-cloud/rio-uikit/OverlayTrigger';
import Tooltip from '@rio-cloud/rio-uikit/Tooltip';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { isDev } from '#configuration/env.ts';

const POI_MANAGE_URL = 'https://admin.geofencing.rio.cloud/#/pois';
const POI_CREATE_URL = 'https://admin.geofencing.rio.cloud/#/pois/create';

type SavePoiDialogMode = 'create' | 'manage';

export type SavePoiCreateParams = {
    lat: number;
    lng: number;
    label?: string;
};

type SavePoiDialogLinkProps = {
    mode: SavePoiDialogMode;
    createParams?: SavePoiCreateParams;
};

export const getPoiAdminUrl = (mode: SavePoiDialogMode, createParams?: SavePoiCreateParams) => {
    if (mode === 'manage') {
        return POI_MANAGE_URL;
    }

    if (!createParams || !Number.isFinite(createParams.lat) || !Number.isFinite(createParams.lng)) {
        return POI_CREATE_URL;
    }

    const params = new URLSearchParams({
        lat: createParams.lat.toString(),
        lng: createParams.lng.toString(),
    });

    const label = createParams.label?.trim();
    if (label) {
        params.set('label', label);
    }

    return `${POI_CREATE_URL}?${params.toString()}`;
};

export const SavePoiDialogLink = ({ mode, createParams }: SavePoiDialogLinkProps) => {
    const intl = useIntl();
    const [showDialog, setShowDialog] = useState(false);
    const poiAdminUrl = getPoiAdminUrl(mode, createParams);
    const poiDialogTitleId = 'intl-msg:ordercom-cloud-frontend.orders.create-dialog.location.save-poi-dialog.title';

    const openPoiDialogLabel = intl.formatMessage({
        id:
            mode === 'manage'
                ? 'intl-msg:ordercom-cloud-frontend.orders.create-dialog.location.manage-pois'
                : 'intl-msg:ordercom-cloud-frontend.orders.create-dialog.location.save-poi',
    });
    const poiDialogTitle = intl.formatMessage({
        id: poiDialogTitleId,

        defaultMessage: 'Manage POIs',
    });
    const openInNewWindowLabel = intl.formatMessage({
        id: 'intl-msg:ordercom-cloud-frontend.orders.create-dialog.location.save-poi-dialog.open-new-window',

        defaultMessage: 'Open in a new window',
    });
    const iframeTitle = intl.formatMessage({
        id: 'intl-msg:ordercom-cloud-frontend.orders.create-dialog.location.save-poi-dialog.iframe-title',

        defaultMessage: 'POI management',
    });
    const openInNewWindow = () => window.open(poiAdminUrl, '_blank', 'noopener,noreferrer');

    const handleOpen = () => {
        if (isDev) {
            openInNewWindow();
            return;
        }

        setShowDialog(true);
    };

    return (
        <>
            <Button
                bsStyle={Button.PRIMARY}
                variant={Button.VARIANT_LINK_INLINE}
                className='text-size-12 text-decoration-none'
                autoTrackingKey={
                    mode === 'manage'
                        ? 'intl-msg:ordercom-cloud-frontend.orders.create-dialog.location.manage-pois'
                        : 'intl-msg:ordercom-cloud-frontend.orders.create-dialog.location.save-poi'
                }
                onClick={handleOpen}
                data-testid='save-poi-dialog-link'
            >
                {openPoiDialogLabel}
            </Button>
            <Dialog
                show={showDialog}
                title={poiDialogTitle}
                bodyClassName='padding-0'
                body={
                    <iframe
                        title={iframeTitle}
                        className='unstyled full-window no-application-header'
                        src={poiAdminUrl}
                    />
                }
                onClose={() => setShowDialog(false)}
                showCloseButton={false}
                bsSize={Dialog.SIZE_FULL_SCREEN}
                data-testid='save-poi-dialog'
                headerButtons={
                    <>
                        <OverlayTrigger
                            placement='bottom'
                            overlay={<Tooltip width='auto'>{openInNewWindowLabel}</Tooltip>}
                        >
                            <Button
                                bsStyle='muted'
                                iconOnly
                                iconName='new-window'
                                autoTrackingKey='ordercom.orders.create-dialog.location.save-poi-dialog.open-new-window'
                                aria-label={openInNewWindowLabel}
                                onClick={openInNewWindow}
                            />
                        </OverlayTrigger>
                        <Button
                            bsStyle={Button.MUTED}
                            iconOnly
                            iconName='remove'
                            autoTrackingKey={`${poiDialogTitleId}.close`}
                            className='modal-header-close close'
                            onClick={() => setShowDialog(false)}
                            aria-label='dialog close button'
                        />
                    </>
                }
            />
        </>
    );
};
